import { Component } from 'preact'
import type { ComponentChildren } from 'preact'
import ui from '../ui.module.css'
import { pageReload } from './pageReload'

interface Props {
  children?: ComponentChildren
  /** A new value (the next screen) clears the error. */
  resetKey: string
}

interface State {
  error: boolean
}

/**
 * Around the current screen (D-066): a render error shows a small panel with Reload instead of
 * unmounting the whole UI to a blank page. Moving to another screen (`resetKey`) clears it; the
 * tree is not keyed, so matchmaking's pedestals still live on into the race (T3.2).
 */
export class ScreenBoundary extends Component<Props, State> {
  override state: State = { error: false }
  /** The screen the error happened on: only another one clears it. */
  private errorKey: string | null = null

  static override getDerivedStateFromError(): Partial<State> {
    return { error: true }
  }

  override componentDidCatch(error: unknown): void {
    this.errorKey = this.props.resetKey
    console.error('[ui] the screen failed to render', error)
  }

  override componentDidUpdate(): void {
    if (this.state.error && this.errorKey !== this.props.resetKey) {
      this.errorKey = null
      this.setState({ error: false })
    }
  }

  override render() {
    if (!this.state.error) return this.props.children
    return (
      <div class={ui.screen} role="alert">
        <div class={ui.panel}>
          <p class={ui.heading}>Something went wrong.</p>
          <p class={ui.status}>Reload the page to get back in.</p>
          <button
            type="button"
            class={`${ui.button} ${ui.primary}`}
            onClick={() => pageReload.reloadNow()}
          >
            Reload
          </button>
        </div>
      </div>
    )
  }
}
